import { Action, ActionCreator, createAction, createReducer, on, props, ReducerTypes } from '@ngrx/store';

export interface ApiAction<T = any, R = any> extends Action {
  request?: T;
  response?: R;
  error?: any;
}

export interface ApiActions<T, R> {
  start: ActionCreator<string, (props: { request: T }) => { request: T } & Action<string>>;
  success: ActionCreator<string, (props: { response: R }) => { response: R } & Action<string>>;
  error: ActionCreator<string, (props: { error: any }) => { error: any } & Action<string>>;
  reset: ActionCreator<string, () => Action<string>>;
}

export const createApiActions = <T, R>(actionPrefix: string): ApiActions<T, R> => {
  return {
    start: createAction(`${actionPrefix} Start`, props<{ request: T }>()),
    success: createAction(`${actionPrefix} Success`, props<{ response: R }>()),
    error: createAction(`${actionPrefix} Error`, props<{ error: any }>()),
    reset: createAction(`${actionPrefix} Reset`),
  };
};

export interface ApiState<R> {
  processing: boolean;
  processed: boolean;
  error: any;
  response: R;
}

/**
 * Creates general ApiState based reducer fields that can be used in coordination with cached state responses in the effect
 */
export const createCachedApiReducer = <T, R>(
  actions: ApiActions<T, R>,
  initialState: ApiState<R> = { processing: false, processed: false, error: null, response: null },
  ...additionalOns: ReducerTypes<ApiState<R>, ActionCreator[]>[]
) => {
  return createReducer(
    initialState,
    on(actions.start, (state) => {
      return {
        ...state,
        processing: true,
        processed: false,
        error: null,
      };
    }),
    on(actions.success, (state, action) => {
      return {
        ...state,
        processing: false,
        processed: true,
        error: null,
        response: action.response,
      };
    }),
    on(actions.error, (state, action) => {
      return {
        ...state,
        processing: false,
        processed: true,
        error: action.error,
        response: null,
      };
    }),
    on(actions.reset, (state) => {
      return {
        ...state,
        processing: false,
        processed: false,
        error: null,
        response: null,
      };
    }),
    ...additionalOns
  );
};
